/**
 * Reading ground height off a built terrain mesh.
 *
 * Extracted from `world.ts` so that feature placement can use it without
 * importing the orchestrator — the renderer contract depends on placement, and
 * the orchestrator depends on the renderer contract.
 */

import type { TerrainMeshData } from './build/terrainmesh.js';

/**
 * Height at a chunk-local position, interpolated across the same triangles the
 * renderer draws.
 *
 * Bilinear interpolation would be wrong here: a quad split into two triangles
 * is not a bilinear patch, and on 14 m cells over rough ground the two can
 * differ by the better part of a metre — enough to see a tree floating.
 */
export function sampleMeshHeight(mesh: TerrainMeshData, x: number, z: number): number {
  const { gridRes, gridHeights, width, depth } = mesh;
  const side = gridRes + 1;

  const fx = clamp01(x / width) * gridRes;
  // Rows are not evenly spaced in metres, but the deviation across one tile is
  // millimetres, so a uniform row index is accurate well past what matters for
  // standing on the ground.
  const fz = clamp01(z / depth) * gridRes;

  const j = Math.min(gridRes - 1, Math.floor(fx));
  const i = Math.min(gridRes - 1, Math.floor(fz));
  const u = fx - j;
  const v = fz - i;

  const a = gridHeights[i * side + j];           // NW
  const b = gridHeights[i * side + j + 1];       // NE
  const c = gridHeights[(i + 1) * side + j];     // SW
  const d = gridHeights[(i + 1) * side + j + 1]; // SE

  // The mesh splits each quad along its NE-SW diagonal, which in (u, v) is the
  // line u + v = 1. Pick the triangle the point actually falls in.
  if (u + v <= 1) return a + u * (b - a) + v * (c - a);
  return d + (1 - v) * (b - d) + (1 - u) * (c - d);
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Retained bytes of a mesh payload, for the cache budget. */
export function estimateMeshBytes(mesh: TerrainMeshData): number {
  return (
    mesh.positions.byteLength +
    mesh.normals.byteLength +
    mesh.uvs.byteLength +
    mesh.indices.byteLength +
    mesh.gridHeights.byteLength
  );
}
